import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getHomeData, getHomeAnnouncement } from "../../../../feature-module/auth/services/apiCalling";

interface Announcement {
  id: number;
  grSubject: string;
  enSubject: string;
  imageName: string;
  imagePath: string;
  tagName: string;
  createdDate: string;
  isActive: boolean;
  isDelete?: boolean;
  lastUpdateBy?: string | null;
  createdBy?: string;
  docId: string;
}

const AnnouncementTicker: React.FC<{ hlanguage: any }> = ({ hlanguage }) => {
  const [data, setData] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [resetTimer, setResetTimer] = useState(0);
  const [fileLoading, setFileLoading] = useState(false);

  // Fetch announcements on component mount
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const response = await getHomeData("announcement");
        
        if (response?.data?.success && response?.data?.data) {
          setData(response.data.data);
          setError(null);
        } else {
          setError("Failed to load announcements");
        }
      } catch (err) {
        setError("Failed to load announcements");
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const next = () => {
    setDirection('next');
    setIndex((i) => (i + 1) % data.length);
    setResetTimer(prev => prev + 1);
  };
  
  const prev = () => {
    setDirection('prev');
    setIndex((i) => (i === 0 ? data.length - 1 : i - 1));
    setResetTimer(prev => prev + 1);
  };

  useEffect(() => {
    if (isPaused || data.length === 0 || loading) return;
    const timer = setInterval(() => {
      setDirection('next');
      setIndex((i) => (i + 1) % data.length);
    }, 7500);
    return () => clearInterval(timer);
  }, [isPaused, resetTimer, data.length, loading]);

  // Function to handle file/link click
  const handleClick = async (e: React.MouseEvent, announcement: Announcement) => {
    e.stopPropagation();
    
    try {
      setFileLoading(true);
      
      // Call the API function to get the file
      const response = await getHomeAnnouncement(announcement.docId, announcement.imageName);
      
      if (response?.data) {
        // Get the file URL from response and open in new tab
        const fileUrl = `${process.env.REACT_APP_BASE_URL}/superadmin/get-image/${announcement.docId}/${announcement.imageName}`;
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Error opening the file. Please try again.');
      }
      
    } catch (error) {
      console.error('Error opening file:', error);
      alert('Error opening the file. Please try again.');
    } finally {
      setFileLoading(false);
    }
  };

//   const stripHtml = (html: string) => {
//     const tmp = document.createElement("DIV");
//     tmp.innerHTML = html;
//     return tmp.textContent || tmp.innerText || "";
//   };

  // Guard clause for empty data (but only after loading is complete)
  if (!loading && (!data || data.length === 0)) {
    return null;
  }

  const currentLanguage = localStorage.getItem("selectedLanguage") || "homeEn";

  const current = data[index];

  return (
    <>
      <style>
        {`
          .news-ticker {
            border: 2px solid #004e15;
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .news-ticker-inner {
            display: flex;
            flex-direction: column;
          }

          @media (min-width: 768px) {
            .news-ticker-inner {
              flex-direction: row;
              align-items: stretch;
            }
          }

          .news-label {
            background: linear-gradient(135deg, #004e15 0%, #006b1d 100%);
            color: #fff;
            font-weight: 600;
            padding: 10px 16px;
            text-align: center;
            font-size: 14px;
            letter-spacing: 0.5px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          @media (min-width: 768px) {
            .news-label {
              min-width: 180px;
              writing-mode: horizontal-tb;
            }
          }

          .news-content-wrapper {
            flex: 1;
            display: flex;
            align-items: center;
            position: relative;
          }

          .news-track {
            overflow: hidden;
            position: relative;
            width: 100%;
            padding: 12px 12px;
            display: flex;
            align-items: center;
          }

          .news-animate {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            width: 100%;
            transform: translateX(0);
            opacity: 1;
          }

          .news-animate.slide-next {
            animation: slideInOutNext 7.5s ease-in-out forwards;
          }

          .news-animate.slide-prev {
            animation: slideInOutPrev 7.5s ease-in-out forwards;
          }

          @keyframes slideInOutNext {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            8% {
              transform: translateX(0);
              opacity: 1;
            }
            92% {
              transform: translateX(0);
              opacity: 1;
            }
            100% {
              transform: translateX(-100%);
              opacity: 0;
            }
          }

          @keyframes slideInOutPrev {
            0% {
              transform: translateX(-100%);
              opacity: 1;
            }
            8% {
              transform: translateX(0);
              opacity: 1;
            }
            92% {
              transform: translateX(0);
              opacity: 1;
            }
            100% {
              transform: translateX(100%);
              opacity: 1;
            }
          }

          .news-badge {
            display: inline-block;
            background: #333;
            color: #fff;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            flex-shrink: 0;
            width: fit-content;
          }

          .news-text {
            font-size: 14px;
            font-weight: 600;
            color: #333;
            line-height: 1.6;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .news-text-clickable {
            cursor: pointer;
            transition: color 0.2s ease;
            position: relative;
          }

          .news-text-clickable:hover {
            color: #004e15;
            text-decoration: underline;
          }

          .news-text-clickable.loading {
            opacity: 0.6;
            cursor: wait;
          }

          @media (min-width: 768px) {
            .news-text {
              font-size: 16px;
            }
            .news-animate {
              flex-direction: row;
              align-items: center;
              gap: 15px;
            }
            .news-track {
              padding: 12px 20px;
            }
          }

          .news-controls {
            background: #f8f9fa;
            padding: 12px 16px;
            display: flex;
            gap: 8px;
            align-items: center;
            justify-content: center;
            border-top: 1px solid #e0e0e0;
            flex-shrink: 0;
          }

          @media (min-width: 768px) {
            .news-controls {
              border-top: none;
              border-left: 1px solid #e0e0e0;
              flex-direction: row;
              padding: 12px 16px;
              min-width: 100px;
            }
          }

          .news-control-btn {
            background: #004e15;
            color: #fff;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 18px;
          }

          .news-control-btn:hover {
            background: #006b1d;
            transform: scale(1.1);
          }

          .news-control-btn:active {
            transform: scale(0.95);
          }

          .news-control-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .news-control-btn:disabled:hover {
            transform: none;
            background: #004e15;
          }
        `}
      </style>

      <div className="container-fluid my-4">
        <div className="news-ticker">
          <div className="news-ticker-inner">
            {/* Label */}
            <div className="news-label">
              {hlanguage?.announcement || "Announcements"} <span><i className="fa fa-bell ms-1"></i></span>
            </div>

            {/* Content */}
            <div className="news-content-wrapper">
              <div className="news-track">
                {loading ? (
                  <div className="news-animate">
                    <span className="news-text">Loading announcements...</span>
                  </div>
                ) : error ? (
                  <div className="news-animate">
                    <span className="news-text" style={{ color: '#d32f2f' }}>{error}</span>
                  </div>
                ) : (
                  <div 
                    key={index} 
                    className={`news-animate slide-${direction}`}
                  >
                    <span className="news-badge">{current.tagName}</span>
                    <span 
                      className={`news-text news-text-clickable ${fileLoading ? 'loading' : ''}`}
                      onClick={(e) => handleClick(e, current)}
                      title="Click to view file"
                    >
                      {currentLanguage==="homeEn" ? current?.enSubject : current.grSubject}
                      {fileLoading && <i className="fa fa-spinner fa-spin ms-2"></i>}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="news-controls">
              <button 
                className="news-control-btn" 
                onClick={prev}
                aria-label="Previous announcement"
                disabled={loading || fileLoading}
              >
                ‹
              </button>
              <button 
                className="news-control-btn" 
                onClick={next}
                aria-label="Next announcement"
                disabled={loading || fileLoading}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnouncementTicker;
